import { api } from './config';
import API_CONFIG from './config';

const STATS_BASE_URL = `${API_CONFIG.BASE_URL}/stats`;

/**
 * Servicio para manejar las estadísticas del marketplace
 */
class StatsService {
  /**
   * Obtiene todas las estadísticas del usuario actual
   * @returns {Promise<Object>} Objeto con todas las estadísticas
   */
  async getAllStats() {
    try {
      const response = await api.get(STATS_BASE_URL);
      return response.data;
    } catch (error) {
      console.error('Error getting all stats:', error);
      throw error;
    }
  }

  /**
   * Registra una nueva visualización de producto
   * @param {string} productId - ID del producto visualizado
   * @returns {Promise<Object>} Respuesta de confirmación
   */
  async recordProductView(productId) {
    try {
      const response = await api.post(`${STATS_BASE_URL}/views`, { productId });
      return response.data;
    } catch (error) {
      console.error('Error recording product view:', error);
      throw error;
    }
  }

  /**
   * Obtiene el número de productos publicados de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Contador de productos publicados
   */
  async getPublishedProducts(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/products/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting published products count:', error);
      throw error;
    }
  }

  /**
   * Obtiene el número de negociaciones activas de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Contador de negociaciones activas
   */
  async getActiveNegotiations(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/negotiations/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting active negotiations count:', error);
      throw error;
    }
  }

  /**
   * Obtiene el número de visualizaciones de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Contador de visualizaciones
   */
  async getViews(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/views/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting views count:', error);
      throw error;
    }
  }

  /**
   * Obtiene el número de búsquedas de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Contador de búsquedas
   */
  async getSearches(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/searches/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting searches count:', error);
      throw error;
    }
  }

  /**
   * Obtiene el índice de satisfacción del cliente de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Porcentaje de satisfacción
   */
  async getCustomerSatisfaction(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/satisfaction/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting customer satisfaction:', error);
      throw error;
    }
  }

  /**
   * Obtiene el ranking en el marketplace de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Posición en el ranking
   */
  async getMarketplaceRanking(userId) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/ranking/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting marketplace ranking:', error);
      throw error;
    }
  }

  /**
   * Compara las estadísticas de dos usuarios
   * @param {string} userId1 - ID del primer usuario
   * @param {string} userId2 - ID del segundo usuario
   * @returns {Promise<Object>} Comparación de estadísticas
   */
  async compareUsers(userId1, userId2) {
    try {
      const response = await api.get(`${STATS_BASE_URL}/compare/${userId1}/${userId2}`);
      return response.data;
    } catch (error) {
      console.error('Error comparing users:', error);
      throw error;
    }
  }

  /**
   * Actualiza las estadísticas en tiempo real
   * @param {Function} callback - Función a ejecutar cuando se actualicen las stats
   * @returns {Function} Función para detener las actualizaciones
   */
  subscribeToUpdates(callback) {
    const intervalId = setInterval(async () => {
      try {
        const stats = await this.getAllStats();
        callback(stats);
      } catch (error) {
        console.error('Error updating stats:', error);
      }
    }, 500000); // Actualiza cada 30 segundos

    // Retorna una función para detener las actualizaciones
    return () => clearInterval(intervalId);
  }

  /**
   * Formatea las estadísticas para mostrar en la UI
   * @param {Object} stats - Objeto con estadísticas crudas
   * @returns {Object} Estadísticas formateadas
   */
  formatStats(stats) {
    return {
      publishedProducts: {
        value: stats.publishedProducts,
        label: 'Productos Publicados',
        description: 'Total de productos activos'
      },
      activeNegotiations: {
        value: stats.activeNegotiations,
        label: 'Negociaciones Activas',
        description: 'En proceso actual'
      },
      visualizations: {
        value: this.formatNumber(stats.visualizations),
        label: 'Visualizaciones',
        description: 'Vistas de productos'
      },
      searches: {
        value: stats.searches,
        label: 'Búsquedas Realizadas',
        description: 'Total de búsquedas'
      },
      customerSatisfaction: {
        value: `${stats.customerSatisfaction}%`,
        label: 'Satisfacción Cliente',
        description: 'Índice de satisfacción'
      },
      marketplaceRanking: {
        value: `#${stats.marketplaceRanking}`,
        label: 'Ranking Marketplace',
        description: 'Posición actual'
      }
    };
  }

  /**
   * Formatea números grandes para mejor visualización
   * @param {number} number - Número a formatear
   * @returns {string} Número formateado
   */
  formatNumber(number) {
    if (number >= 1000000) {
      return `${(number / 1000000).toFixed(1)}M`;
    }
    if (number >= 1000) {
      return `${(number / 1000).toFixed(1)}k`;
    }
    return number.toString();
  }
}

// Creamos una instancia y la asignamos a una variable
const statsServiceInstance = new StatsService();

// Exportamos la instancia única del servicio
export default statsServiceInstance;